// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Coverage Service
 *
 * Typed wrappers over the BFF's coverage proxy (/api/v1/coverage), which
 * fronts the Onboarding Coverage Service for the site onboarding wizard:
 * session lifecycle, site profile, document checklist with bypass, submit,
 * and the staged-publish review/approval surface.
 */

import { ENDPOINTS } from '../utils/constants';
import { del, get, post, put, type ApiError } from './api';
import type {
  ApprovalRequest,
  ApprovalResponse,
  ChecklistResponse,
  CoverageSession,
  CreateCoverageSessionRequest,
  SiteProfile,
  StagingSummaryResponse,
} from '../types';

/**
 * The site's active onboarding session, or null when it has none (the BFF
 * returns 404 for "no active session", which is a normal state mapped to
 * "start at step one" — never an error banner).
 */
export async function getActiveSession(siteId: string): Promise<CoverageSession | null> {
  try {
    return await get<CoverageSession>(ENDPOINTS.COVERAGE_ACTIVE_SESSION(siteId));
  } catch (err) {
    if ((err as ApiError).status === 404) return null;
    throw err;
  }
}

export function createSession(
  siteId: string,
  request: CreateCoverageSessionRequest
): Promise<CoverageSession> {
  return post<CoverageSession>(ENDPOINTS.COVERAGE_SESSIONS(siteId), request);
}

export function getSession(siteId: string, sessionId: string): Promise<CoverageSession> {
  return get<CoverageSession>(ENDPOINTS.COVERAGE_SESSION(siteId, sessionId));
}

export function updateSiteProfile(
  siteId: string,
  sessionId: string,
  profile: SiteProfile
): Promise<CoverageSession> {
  return put<CoverageSession>(ENDPOINTS.COVERAGE_SITE_PROFILE(siteId, sessionId), profile);
}

export function getChecklist(siteId: string, sessionId: string): Promise<ChecklistResponse> {
  return get<ChecklistResponse>(ENDPOINTS.COVERAGE_CHECKLIST(siteId, sessionId));
}

export function bypassItem(
  siteId: string,
  sessionId: string,
  itemId: string,
  reason: string,
  actor: string
): Promise<CoverageSession> {
  return post<CoverageSession>(ENDPOINTS.COVERAGE_BYPASS(siteId, sessionId, itemId), {
    reason,
    actor,
  });
}

export function removeBypass(
  siteId: string,
  sessionId: string,
  itemId: string,
  actor: string
): Promise<null> {
  // The coverage contract records the acting user on unbypass, so the
  // DELETE carries a body (api.ts forwards it via options).
  return del<null>(ENDPOINTS.COVERAGE_BYPASS(siteId, sessionId, itemId), {
    body: JSON.stringify({ actor }),
  });
}

export function submitSession(siteId: string, sessionId: string): Promise<CoverageSession> {
  return post<CoverageSession>(ENDPOINTS.COVERAGE_SUBMIT(siteId, sessionId));
}

export function getStaging(siteId: string, sessionId: string): Promise<StagingSummaryResponse> {
  return get<StagingSummaryResponse>(ENDPOINTS.COVERAGE_STAGING(siteId, sessionId));
}

export function applyApproval(
  siteId: string,
  sessionId: string,
  request: ApprovalRequest
): Promise<ApprovalResponse> {
  return post<ApprovalResponse>(ENDPOINTS.COVERAGE_APPROVAL(siteId, sessionId), request);
}
